import React, { useState, useEffect, useRef, useMemo } from "react";
import JoditEditor from "jodit-react";
import AdminNav from "../../../components/AdminNav/AdminNav";
import "../Adminupload/UploadArticle.css";
import { useParams, useNavigate } from "react-router-dom";
import { set } from "jodit/esm/core/helpers";

export default function Adminedit() {
  const { slug } = useParams(); // get article id from route
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [content, setContent] = useState("");
  const [tags, setTags] = useState("");
  const [thumbnail, setThumbnail] = useState(null);
  const [type, setType] = useState("");
  const [postTypes, setPostTypes] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [id , setId] = useState(null);
  const editor = useRef(null);
  const token = localStorage.getItem("token");
  const [short_description, setShortDescription] = useState("");
  const apiUrl = process.env.REACT_APP_API_URL;
  const storageUrl = process.env.REACT_APP_STORAGE_URL; 

  const config = useMemo(() => ({
    height: 'auto',
    minHeight: 500,
    toolbarAdaptive: false,
    controls: {
      paragraph: {
        list: {
          p: 'Normál',
          h1: 'Címsor 1',
          h2: 'Címsor 2',
          h3: 'Tartalomjegyzék',
          h4: 'Címsor 4',
          h5: 'Címsor 5',
          h6: 'Címsor 6',
          blockquote: 'Idézet'
        }
      },
      classSpan: {
        list: {
          toc: 'Tartalomjegyzék',
          lead: 'Bevezető szöveg',
          warning: 'Képaláírás',
          error: 'Link'
        }
      }
    },
    showCharsCounter: true,
    showWordsCounter: true,
    statusbar: true,
    uploader: {
      insertImageAsBase64URI: false,
      url: `${apiUrl}/upload-image`,
      format: 'json',
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
        Accept: 'application/json'
      },
      prepareData: function (data) {
        // Jodit provides this.file when uploading a single file
        // We'll send it as 'file' which our backend now accepts
        if (this.file) {
          data.append('file', this.file);
        }
        return data;
      },
      isSuccess: function (resp) {
        // Treat as success when backend returns success true or provides data.files
        return !!(resp && (resp.success === true || (resp.data && Array.isArray(resp.data.files))));
      },
      getMsg: function (resp) {
        if (!resp) return 'Ismeretlen hiba';
        if (Array.isArray(resp.messages)) return resp.messages.join(' ');
        if (resp.msg && resp.msg.join !== undefined) return resp.msg.join(' ');
        if (typeof resp.msg === 'string') return resp.msg;
        return 'Feltöltési hiba';
      },
      process: (resp) => {
        // Normalize to { files: [url, ...] }
        if (resp && resp.data && Array.isArray(resp.data.files)) {
          const urls = resp.data.files.filter(u => typeof u === 'string' && u.length > 0);
          return { files: urls };
        }
        return { files: [] };
      },
      defaultHandlerSuccess: function (data /* normalized by process */) {
        const files = (data && data.files) || [];
        if (files.length) {
          this.selection.insertImage(files[0], null, 250);
        }
      },
      defaultHandlerError: function (resp) {
        this.events.fire('errorPopap', this.i18n((resp && (resp.msg || resp.messages)) || 'Feltöltési hiba'));
      }
    },
    buttons: [
      "font", "fontsize", "paragraph", "lineHeight", "classSpan", "spellcheck",
      "bold", "italic", "underline", "strikethrough", "superscript", "subscript", "|",
      "ul", "ol", "indent", "outdent", "|",
      "image", "video", "file", "table", "link", "cut", "copy", "paste", "selectall", "|",
      "align", "left", "center", "right", "justify", "undo", "redo", "hr", "eraser", "copyformat", "source", "preview", "find"
    ]
  }), [apiUrl, token]);

  useEffect(() => {
    // Fetch post types
    fetch(`${apiUrl}/post_types`,{
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
        Accept: "application/json",
      },
      credentials: "include",
    })
      .then(res => res.json())
      .then(data => setPostTypes(data))
      .catch(() => setPostTypes([]));

    // Fetch article data
    fetch(`${apiUrl}/admin/article/${slug}`,{
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
        Accept: "application/json",
      },
      credentials: "include",
    })
      .then(async (res) => {
        if (!res.ok) {
          // Try to extract message from response
          let body = null;
          try { body = await res.json(); } catch (e) { body = null; }
          if (res.status === 403) {
            setMessage(body && body.error ? body.error : 'Nincs jogosultság a cikk megtekintéséhez.');
          } else if (res.status === 404) {
            setMessage('A cikk nem található.');
          } else {
            setMessage('Nem sikerült betölteni a cikket.');
          }
          return null;
        }
        return res.json();
      })
      .then(data => {
        if (!data) return;
        setTitle(data.title || "");
        setAuthor(data.author || "");
        setContent(data.content || "");
        setType(data.post_type ?? "");
        setShortDescription(data.short_description || "");
        setId(data.id || null);
        
        // Set initial thumbnail from backend if available
        if (data.thumbnail) {
          setThumbnail(`${storageUrl}${data.thumbnail}`);
        }
        // Ensure Jodit hides placeholder and updates counters when content is set programmatically
        setTimeout(() => {
          const ed = editor.current;
          try {
            if (ed) {
              // assign value to editor instance and notify it changed
              ed.value = data.content || '';
              ed.events && ed.events.fire && ed.events.fire('change');
              if (typeof ed.synchronizeValues === 'function') ed.synchronizeValues();
              if (typeof ed.update === 'function') ed.update();
            }
          } catch (_) {}
        }, 0);
      })
      .catch(() => setMessage("Nem sikerült betölteni a cikket."));

    
  }, [slug,apiUrl,storageUrl,token]);

  useEffect(() => {
    // Fetch all labels for post
    if (!id) return;
    fetch(`${apiUrl}/posts/${id}/labels/all`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
        Accept: "application/json",
      },
      credentials: "include",
    })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          // If response is array of label objects with 'name' property
          setTags(data.map(l => l.name).join(", "));
        } else if (Array.isArray(data.labels)) {
          // If response is { labels: [...] }
          setTags(data.labels.map(l => l.name).join(", "));
        }
      })
      .catch(() => {});
  }, [apiUrl, token, id]);

  

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    // Guard against undefined content when no edits were made
    const editorInstance = editor.current;
    const safeContent = (typeof content === 'string')
      ? content
      : (editorInstance && typeof editorInstance.value === 'string' ? editorInstance.value : '');
    const formData = new FormData();
    formData.append("title", title);
    formData.append("author", author);
    formData.append("content", safeContent);
    formData.append("tags", tags);
    formData.append("type", type);
    formData.append("short_description", short_description);
    if (thumbnail) formData.append("thumbnail", thumbnail);
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${apiUrl}/admin/article/edit/${id}`, {
        method: "Post",
        body: formData,
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });
      if (res.ok) {
        setMessage("Sikeres szerkesztés!");
        setLoading(false);
        // Navigate to post control after successful edit
        navigate('/admin/postcontrol');
      } else {
        setMessage("Hiba történt a szerkesztéskor.");
        setLoading(false);
      }
    } catch {
      setMessage("Hiba történt a szerkesztéskor.");
      setLoading(false);
    }
  };

  return (
    <div>
      <AdminNav />
      <div className="upload-article-container">
        <h2>Cikk szerkesztése</h2>
        <form onSubmit={handleSubmit} className="upload-article-form">
          <label>Szerző:
            <input type="text" value={author} onChange={e => setAuthor(e.target.value)} required />
          </label>
          <label>Cím:
            <input type="text" value={title} onChange={e => setTitle(e.target.value)} required />
          </label>
          <label>Alcím:
            <input type="text" value={short_description} onChange={e => setShortDescription(e.target.value)} placeholder="(nem kötelező)" />
          </label>
          <label>Típus:
            <select value={String(type)} onChange={e => setType(e.target.value)}>
              <option value="">Válassz típust</option>
              {postTypes.map(pt => (
                <option key={pt.id} value={String(pt.id)}>{pt.type}</option>
              ))}
            </select>
          </label>
          <label>Tartalom:
            <JoditEditor
              ref={editor}
              value={content}
              config={config}
              onChange={(newContent) =>
                setContent(prev =>
                  typeof newContent === 'string'
                    ? newContent
                    : (editor.current && typeof editor.current.value === 'string'
                        ? editor.current.value
                        : (typeof prev === 'string' ? prev : ''))
                )
              }
              className="jodit-editor"
            />
          </label>
          <label>Címkék (vesszővel elválasztva):
            <input type="text" value={tags} onChange={e => setTags(e.target.value)} />
          </label>
          <label>Borítókép:
            <input type="file" accept="image/*" onChange={e => setThumbnail(e.target.files[0])} />
            {thumbnail && (
              <div style={{ marginTop: '10px' }}>
                <img
                  src={typeof thumbnail === 'string' ? thumbnail : URL.createObjectURL(thumbnail)}
                  alt="Thumbnail preview"
                  style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '8px', border: '1px solid #ccc' }}
                />
              </div>
            )}
          </label>
          <button type="submit" className="form-submit" disabled={loading}>{loading ? "Mentés..." : "Mentés"}</button>
        </form>
        {message && <div className="upload-article-message">{message}</div>}
      </div>
    </div>
  );
}
